import { useAuthenticatedApi, useLocalStorage } from "@webng/react-app-common";
import { ApiClient } from "@webng/tickaroo-api";
import { AnswerSchema, PollSchema } from "@webng-types/interactive-backend";
import { Dispatch, useCallback } from "react";
import { generateRandomId } from "@webng/uniqueid-generator";
import {
  MutationFunction,
  QueryFunction,
  UseMutateFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

interface PollApiProps {
  pollId: string;
  organizationId: string;
}

const USER_IDS: Record<string,string> = {}
function generateUniqueUserIdByPoll(pollId: string){
  if(USER_IDS[pollId]) return USER_IDS[pollId];
  const id = generateRandomId("tik_vote_id")
  USER_IDS[pollId] = id;
  return id;
}

export function useSaveAnswer(pollId: string) {
  const [answer, saveAnswer] = useLocalStorage<{
    userId: string;
    answerId?: string;
    created_at?: string;
  }>(`tik_vote_id_${pollId}`, {
    userId: generateUniqueUserIdByPoll(pollId),
    created_at: new Date().toISOString(),
  });

  return [answer, saveAnswer] as [
    { userId: string; answerId?: string },
    Dispatch<{ userId: string; answerId?: string }>
  ];
}

export type PollQueryResult = {
  poll: PollSchema;
  answers: AnswerSchema[];
  vote?: string;
};
export type PollQueryKey = ["pollQuery", string];

export function useFetchPoll({
  pollId,
  organizationId,
}: PollApiProps): [PollQueryResult | undefined, boolean, boolean, any] {
  const apiClient = useAuthenticatedApi();

  const queryFn: QueryFunction<PollQueryResult, PollQueryKey> = useCallback(
    async ({ queryKey }) => {
      const [, pollId] = queryKey;
      return apiClient.get<PollQueryResult>(ApiClient.API_INTERACTIVE_V1_POLLS_FETCH, {
        pollId, organizationId
      });
    },
    [apiClient, organizationId]
  );

  const { data, isError, isLoading, refetch } = useQuery<
    PollQueryResult,
    Object,
    PollQueryResult,
    PollQueryKey
  >({
    queryKey: ["pollQuery", pollId],
    queryFn: queryFn,
    retry: true,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
    staleTime: 60000,
  });

  return [data, isError, isLoading, refetch];
}

export function useCastVote({
  pollId,
  organizationId,
}: PollApiProps): [boolean, boolean, UseMutateFunction<void, Object, string>] {
  const apiClient = useAuthenticatedApi();
  const queryClient = useQueryClient();
  const [answer, saveAnswer] = useSaveAnswer(pollId);

  const {userId} = answer;

  const mutationFn: MutationFunction<void, string> = useCallback(
    async (answerId) => {
      return apiClient.post<void>(
        ApiClient.API_INTERACTIVE_V1_POLLS_SUBMIT,
        { userId, answerId },
        { organizationId, pollId }
      );
    },
    [apiClient, organizationId, pollId, userId]
  );

  const { mutate, isError, isPending, reset } = useMutation<
    void,
    Object,
    string
  >({
    mutationFn,
    retry: 0,
    onMutate: () => {
      reset();
    },
    onSuccess: (_, answerId) => {
      saveAnswer({...answer, answerId})

      queryClient.setQueryData<PollQueryResult | undefined>(
        ["pollQuery", pollId],
        (oldData: PollQueryResult | undefined) => {
          const answers = oldData?.answers;
          const foundAnswer = answers?.find(
            (answer) => answer.answerId === answerId
          );
          if (foundAnswer) foundAnswer.count += 1;

          return oldData
            ? {
                ...oldData,
                vote: answerId,
                answers: answers && foundAnswer ? answers : oldData.answers,
              }
            : undefined;
        }
      );
    },
  });

  return [isError, isPending, mutate];
}
